import { useEffect, useState } from 'react';
import type { BatchTask } from '@avs/contract';
import { useApp } from '../state/AppContext.js';
import { useApiPolled } from '../hooks/usePolling.js';
import { showToast } from '../hooks/useToasts.js';
import { apiClient } from '../api/client.js';
import { Icon } from '../icons/Icon.js';
import { Button } from '../components/Button.js';
import { Card, CardHead } from '../components/Card.js';
import { StatusTag } from '../components/Tag.js';
import { EmptyState } from '../components/EmptyState.js';
import { relativeTime } from '../utils/time.js';
import { API_MODE } from '../api/client.js';
import type { IconName } from '../icons/registry.js';

const KIND_ICON: Record<BatchTask['kind'], IconName> = {
  object: 'object',
  scene: 'image',
  person: 'user',
};

const KIND_LABEL: Record<BatchTask['kind'], string> = {
  object: '物体替换',
  scene: '场景替换',
  person: '人物替换',
};

export function History() {
  const { actions } = useApp();

  const [shouldPoll, setShouldPoll] = useState(false);
  const { data, loading, refresh } = useApiPolled(
    () => apiClient.batchOverview(), 3000, [], shouldPoll,
  );
  useEffect(() => {
    const next = (data?.running.length ?? 0) > 0;
    setShouldPoll((cur) => cur === next ? cur : next);
  }, [data]);

  return (
    <>
      {API_MODE !== 'real' && (
        <div className="alert alert-warn">
          <Icon name="info" size={18} />
          <div className="alert-body">
            <div className="alert-title">演示模式</div>
            <div>当前为本地示例数据。连接真实后端后（VITE_API_MODE=real），任务管理会展示 D1 里的真实批次状态。</div>
          </div>
        </div>
      )}

      <Card>
        <CardHead
          title="概览"
          desc="快速开始任务，并追踪进行中和最近批次。"
          right={
            <div className="row">
              <Button
                variant="primary"
                leftIcon={<Icon name="add" size={16} />}
                onClick={() => actions.navigate('object')}
              >新建任务</Button>
              <Button
                leftIcon={<Icon name="refresh" size={16} />}
                onClick={() => { refresh(); showToast('已刷新'); }}
              >刷新</Button>
            </div>
          }
        />
        <div className="stat-row two">
          <div className="stat-card">
            <div className="stat-num">{loading ? '—' : (data?.runningSubJobs ?? 0)}</div>
            <div className="stat-label">进行中的子任务</div>
          </div>
          <div className="stat-card">
            <div className="stat-num">{loading ? '—' : (data?.recentlyDoneBatches ?? 0)}</div>
            <div className="stat-label">最近可查看结果的批次</div>
          </div>
        </div>
      </Card>

      <div className="two-col">
        <Card>
          <div className="block-title" style={{ marginBottom: 'var(--space-s)' }}>进行中的任务</div>
          {loading ? (
            <EmptyState icon="refresh" title="加载中" variant="plain" />
          ) : (data?.running.length ?? 0) > 0 ? (
            (data?.running ?? []).map((b) => <BatchMini key={b.id} batch={b} onClick={() => actions.navigate(b.kind)} />)
          ) : (
            <EmptyState
              icon="video"
              title="暂无任务"
              sub="当前没有运行中的批次。"
              variant="plain"
            />
          )}
        </Card>
        <Card>
          <div className="block-title" style={{ marginBottom: 'var(--space-s)' }}>最近任务</div>
          {loading ? (
            <EmptyState icon="refresh" title="加载中" variant="plain" />
          ) : (data?.recent.length ?? 0) > 0 ? (
            (data?.recent ?? []).map((b) => <BatchMini key={b.id} batch={b} onClick={() => actions.navigate(b.kind)} />)
          ) : (
            <EmptyState
              icon="video"
              title="暂无任务"
              sub="最近还没有批次记录。"
              variant="plain"
            />
          )}
        </Card>
      </div>
    </>
  );
}

function BatchMini({ batch, onClick }: { batch: BatchTask; onClick: () => void }) {
  const jobsTotal = batch.jobs.done + batch.jobs.running + batch.jobs.failed;
  return (
    <div className="list-item" onClick={onClick} role="button" tabIndex={0}>
      <div className="li-ic">
        <Icon name={KIND_ICON[batch.kind]} size={18} />
      </div>
      <div className="li-main">
        <div className="li-name">{batch.name}</div>
        <div className="li-meta">
          {batch.id} · {KIND_LABEL[batch.kind]} · Job {jobsTotal}（完成 {batch.jobs.done}）· {relativeTime(batch.updatedAt)}
        </div>
      </div>
      <StatusTag status={batch.status} />
    </div>
  );
}
